/**
 * 名場面の録画（Lv10 以上ができた瞬間を、前後あわせて数秒の動画に残す）。
 *
 * ショート動画（縦 9:16）にそのまま出せる形で録る（2026-10-04）：
 *
 *   ┌──────────────┐
 *   │ ご当地めしマージ │  上の帯：題名・県名・いま映っている景色の名前
 *   │ 福岡県・〇〇    │  合体の瞬間からは「Lv10 ○○ができた！」も出す
 *   ├──────────────┤
 *   │              │
 *   │  ゲーム画面   │  中央：盤面（2:3）を縮めて置く。余白は盤面をぼかし気味に敷く
 *   │              │
 *   ├──────────────┤
 *   │ スコア 12,345  │  下の帯：スコアとサイトの住所
 *   │ gotochi-…    │
 *   └──────────────┘
 *
 * 音（効果音・合成BGM）も入れる。AudioEngine の出口の手前で分岐を1本足して受け取るので、
 * スピーカーから鳴る音は変わらない。
 *
 * MediaRecorder は「直前の数秒だけ」を取り出せない（途中から切り出した断片は単独で再生できない）。
 * そこで録画機を2台、半周ずらして回し続ける。
 *
 *   1台目 |----10秒----|----10秒----|
 *   2台目       |----10秒----|----10秒----|
 *
 * いつ「残して」と言われても、どちらか一方は必ず5秒以上たまっている。
 * 合体の1.5秒後に、長くたまっているほうを止めて、それを1本の動画にする。
 *
 * 端末の負荷があるので設定で切り替える（既定は切）。対応していないブラウザでは何もしない。
 */

import { track } from '../meta/telemetry';
import { isNativeApp } from '../platform/native';
import { nativeShareFile } from '../platform/nativeShare';

const SEGMENT_MS = 10_000;
/** 合体のあと、動画を締めるまで（「Lv10 ○○ができた！」が読めるだけの長さ）。 */
const AFTER_MS = 2_500;
const FPS = 30;

/** 縦長の動画の大きさ（9:16）。 */
export const HIGHLIGHT_SIZE = { width: 720, height: 1280 } as const;
const TOP_BAND = 170;
const BOTTOM_BAND = 170;
const SITE_HOST = 'gotochi-meshi-merge.pages.dev';

export function canRecordHighlights(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function'
  );
}

function pickMimeType(withAudio: boolean): string | undefined {
  const candidates = withAudio
    ? [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm',
      ]
    : ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'];
  return candidates.find((type) => MediaRecorder.isTypeSupported?.(type));
}

/** 縦長の画面の中で、盤面（cw × ch）を置く場所。上下の帯のあいだに収まる最大の大きさ。 */
export function highlightBoardRect(
  cw: number,
  ch: number,
  size: { width: number; height: number } = HIGHLIGHT_SIZE,
): { x: number; y: number; width: number; height: number } {
  const availW = size.width - 32;
  const availH = size.height - TOP_BAND - BOTTOM_BAND;
  const scale = Math.min(availW / Math.max(1, cw), availH / Math.max(1, ch));
  const width = Math.round(cw * scale);
  const height = Math.round(ch * scale);
  return {
    x: Math.round((size.width - width) / 2),
    y: Math.round(TOP_BAND + (availH - height) / 2),
    width,
    height,
  };
}

/** 帯に書く文字。 */
export interface HighlightInfo {
  regionName: string;
  /** いま映っている景色の名前（無ければ県名だけ）。 */
  sceneName?: string | null;
  score: number;
}

export interface HighlightRecorderOptions {
  /** 帯に書く文字（毎フレーム読む）。 */
  info: () => HighlightInfo;
  /**
   * ゲームが1枚描き終わるたびに呼んでもらう仕組み（Phaser の postrender）。
   * WebGL の画面は描いた直後でないと読み出せないため。無ければ requestAnimationFrame で代わりに描く。
   */
  onFrame?: (draw: () => void) => () => void;
  /** 録画用の音。open で取り出し、録り終えたら close。 */
  audio?: { open(): MediaStream | null; close(): void };
}

interface Slot {
  recorder: MediaRecorder | null;
  chunks: Blob[];
  startedAt: number;
  keep: boolean;
  timer?: number;
}

export interface HighlightClip {
  blob: Blob;
  /** 拡張子（mp4 / webm）。 */
  ext: string;
  level: number;
  /** その段の料理の名前（帯と、保存の確認に使う）。 */
  name?: string;
  /** 共有・保存を一度でも押したか。 */
  saved?: boolean;
}

/** 上の帯の「Lv10 ○○ができた！」。 */
export function highlightBanner(level: number, name?: string): string {
  return name ? `Lv${level} ${name}ができた！` : `Lv${level}ができた！`;
}

export class HighlightRecorder {
  private stream: MediaStream | null = null;
  private readonly slots: Slot[] = [
    { recorder: null, chunks: [], startedAt: 0, keep: false },
    { recorder: null, chunks: [], startedAt: 0, keep: false },
  ];
  private running = false;
  private staggerTimer?: number;
  private pendingLevel = 0;
  private pendingName: string | undefined;
  /** 上の帯に出す「Lv10 ○○ができた！」（合体の瞬間から、動画を締めるまで）。 */
  private banner: string | null = null;
  private mimeType: string | undefined;
  /** 縦長に合成する画面（ページには置かない）の描き先。 */
  private ctx2d: CanvasRenderingContext2D | null = null;
  private unsubscribeFrame: (() => void) | null = null;
  private rafId = 0;
  private lastDrawAt = 0;

  /** いちばん新しい名場面（このプレイのもの）。 */
  clip: HighlightClip | null = null;
  onClip?: (clip: HighlightClip) => void;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly options: HighlightRecorderOptions = { info: () => ({ regionName: '', score: 0 }) },
  ) {}

  start(): void {
    if (this.running || !canRecordHighlights()) return;
    try {
      const composite = document.createElement('canvas');
      composite.width = HIGHLIGHT_SIZE.width;
      composite.height = HIGHLIGHT_SIZE.height;
      const ctx = composite.getContext('2d');
      if (!ctx) return;
      this.ctx2d = ctx;
      this.draw(true);
      const video = composite.captureStream(FPS);
      const audio = this.options.audio?.open() ?? null;
      const tracks = [...video.getVideoTracks(), ...(audio?.getAudioTracks() ?? [])];
      this.stream = new MediaStream(tracks);
      this.mimeType = pickMimeType(tracks.length > 1);
    } catch {
      this.options.audio?.close();
      return;
    }
    // ゲームが描き終わるたびに縦長の画面へ写す
    const draw = () => this.draw();
    if (this.options.onFrame) {
      this.unsubscribeFrame = this.options.onFrame(draw);
    } else {
      const loop = () => {
        draw();
        this.rafId = window.requestAnimationFrame(loop);
      };
      this.rafId = window.requestAnimationFrame(loop);
    }
    this.running = true;
    this.startSlot(0);
    this.staggerTimer = window.setTimeout(() => this.startSlot(1), SEGMENT_MS / 2);
  }

  stop(): void {
    this.running = false;
    window.clearTimeout(this.staggerTimer);
    for (const slot of this.slots) {
      window.clearTimeout(slot.timer);
      if (slot.recorder && slot.recorder.state !== 'inactive' && !slot.keep) {
        slot.recorder.ondataavailable = null;
        slot.recorder.onstop = null;
        slot.recorder.stop();
      }
      if (!slot.keep) slot.recorder = null;
    }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.unsubscribeFrame?.();
    this.unsubscribeFrame = null;
    window.cancelAnimationFrame(this.rafId);
    this.options.audio?.close();
  }

  /** 縦長の画面を1枚描く（30fps に間引く）。 */
  private draw(force = false): void {
    const ctx = this.ctx2d;
    if (!ctx) return;
    const now = performance.now();
    if (!force && now - this.lastDrawAt < 1000 / FPS - 2) return;
    this.lastDrawAt = now;
    const { width: W, height: H } = HIGHLIGHT_SIZE;
    const game = this.canvas;
    const cw = game.width || 480;
    const ch = game.height || 720;
    const info = this.options.info();

    // 下地：盤面を画面いっぱいに薄く敷いて、暗くする
    ctx.fillStyle = '#1a1310';
    ctx.fillRect(0, 0, W, H);
    try {
      const cover = Math.max(W / cw, H / ch);
      ctx.globalAlpha = 0.35;
      ctx.drawImage(game, (W - cw * cover) / 2, (H - ch * cover) / 2, cw * cover, ch * cover);
    } catch {
      /* 読めない瞬間は飛ばす */
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(16, 10, 8, 0.6)';
    ctx.fillRect(0, 0, W, H);

    // 中央：盤面
    const rect = highlightBoardRect(cw, ch);
    try {
      ctx.drawImage(game, rect.x, rect.y, rect.width, rect.height);
    } catch {
      /* noop */
    }
    ctx.strokeStyle = 'rgba(242, 193, 78, 0.6)';
    ctx.lineWidth = 3;
    ctx.strokeRect(rect.x - 1.5, rect.y - 1.5, rect.width + 3, rect.height + 3);

    // 上の帯：題名と、県名・景色の名前（合体の瞬間からは「Lv10 ○○ができた！」も）
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const place = info.sceneName ? `${info.regionName}・${info.sceneName}` : info.regionName;
    if (this.banner) {
      fitText(ctx, 'ご当地めしマージ', W / 2, 30, W - 48, 26, '#f2c14e');
      fitText(ctx, this.banner, W / 2, 84, W - 48, 50, '#ffe28a');
      if (place) fitText(ctx, place, W / 2, 140, W - 48, 28, '#fff8ee');
    } else {
      fitText(ctx, 'ご当地めしマージ', W / 2, 62, W - 48, 48, '#f2c14e');
      if (place) fitText(ctx, place, W / 2, 124, W - 48, 34, '#fff8ee');
    }

    // 下の帯：スコアとサイト
    fitText(ctx, `スコア ${info.score.toLocaleString('ja-JP')}`, W / 2, H - 108, W - 48, 50, '#fff8ee');
    fitText(ctx, SITE_HOST, W / 2, H - 48, W - 48, 28, 'rgba(255, 248, 238, 0.8)');
  }

  /** 新しいプレイの始まり。前のプレイの名場面は捨てる。 */
  resetClip(): void {
    this.clip = null;
    this.banner = null;
  }

  /** 大きな合体があった。少し待ってから、たまっているほうを動画にする。 */
  capture(level: number, name?: string): void {
    if (!this.running) return;
    // 同じ瞬間に何度も呼ばれたら、いちばん大きいレベルだけ
    if (this.pendingLevel > 0) {
      if (level > this.pendingLevel) {
        this.pendingLevel = level;
        this.pendingName = name;
        this.banner = highlightBanner(level, name);
      }
      return;
    }
    this.pendingLevel = level;
    this.pendingName = name;
    this.banner = highlightBanner(level, name);
    window.setTimeout(() => {
      const target = [...this.slots]
        .filter((s) => s.recorder && s.recorder.state === 'recording')
        .sort((a, b) => a.startedAt - b.startedAt)[0];
      if (!target || !target.recorder) {
        this.pendingLevel = 0;
        this.banner = null;
        return;
      }
      target.keep = true;
      window.clearTimeout(target.timer);
      target.recorder.stop();
    }, AFTER_MS);
  }

  private startSlot(index: number): void {
    if (!this.running || !this.stream) return;
    const slot = this.slots[index];
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(this.stream, {
        mimeType: this.mimeType,
        videoBitsPerSecond: 4_000_000,
        audioBitsPerSecond: 128_000,
      });
    } catch {
      this.stop();
      return;
    }
    slot.recorder = recorder;
    slot.chunks = [];
    slot.keep = false;
    slot.startedAt = performance.now();
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) slot.chunks.push(event.data);
    };
    recorder.onstop = () => {
      if (slot.keep && slot.chunks.length > 0) {
        const type = recorder.mimeType || this.mimeType || 'video/webm';
        const clip: HighlightClip = {
          blob: new Blob(slot.chunks, { type }),
          ext: type.includes('mp4') ? 'mp4' : 'webm',
          level: this.pendingLevel,
          name: this.pendingName,
        };
        // 同じプレイでもっと大きな合体があれば、そちらを残す
        if (!this.clip || clip.level >= this.clip.level) {
          this.clip = clip;
          this.onClip?.(clip);
        }
        this.pendingLevel = 0;
        this.banner = null;
      }
      slot.keep = false;
      slot.chunks = [];
      // 止めた録画機はすぐ回し直す
      if (this.running) this.startSlot(index);
    };
    recorder.start();
    slot.timer = window.setTimeout(() => {
      if (recorder.state !== 'inactive' && !slot.keep) recorder.stop();
    }, SEGMENT_MS);
  }
}

/** 帯の文字。幅に収まらなければ小さくする。 */
function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  size: number,
  color: string,
): void {
  let px = size;
  const family = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", system-ui, sans-serif';
  ctx.font = `700 ${px}px ${family}`;
  while (px > 14 && ctx.measureText(text).width > maxWidth) {
    px -= 2;
    ctx.font = `700 ${px}px ${family}`;
  }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

/** 名場面を共有シートへ渡す。渡せない端末では保存にする。 */
export async function shareHighlight(clip: HighlightClip, fileBase: string): Promise<void> {
  track('share', { detail: 'video' });
  clip.saved = true;
  if (isNativeApp()) {
    await nativeShareFile(clip.blob, `${fileBase}.${clip.ext}`);
    return;
  }
  const file = new File([clip.blob], `${fileBase}.${clip.ext}`, { type: clip.blob.type });
  const data: ShareData = { files: [file] };
  if (typeof navigator.share === 'function' && navigator.canShare?.(data)) {
    try {
      await navigator.share(data);
      return;
    } catch {
      // キャンセルされたら保存にはしない
      return;
    }
  }
  const url = URL.createObjectURL(clip.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 5000);
}
